import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { UsageClassification } from '#pie/internal/api/pie-bff';
import { CustomerImpact, useGetMainCategory } from '#pie/internal/api/pie-bff';
import { typedFormFields } from '#pie/utils/typedFormFields';

export const categoriesValidationSchema = z.object({
  mainCategoryId: z.string().min(1),
  subCategoryId: z.string().min(1),
  customerImpact: z.enum([CustomerImpact.Yes, CustomerImpact.No]).optional(),
});

type CategoriesForm = z.infer<typeof categoriesValidationSchema>;

const { FormDropdown } = typedFormFields<CategoriesForm>();

export interface TicketCategoriesFieldsProps {
  usageClassification?: UsageClassification;
  onAttachmentRequiredChange?: (required: boolean) => void;
}
export const TicketCategoriesFields = ({
  usageClassification = 'None',
  onAttachmentRequiredChange,
}: TicketCategoriesFieldsProps) => {
  const { t } = useTranslation();
  const { t: transtic } = useTranslation('ticket');
  const { watch, resetField } = useFormContext<CategoriesForm>();
  const { data: categoryData, isLoading: isLoadingCategories } =
    useGetMainCategory();

  const category = watch('mainCategoryId');
  const subCategory = watch('subCategoryId');

  const categoryItems = useMemo(
    () => categoryData?.items ?? [],
    [categoryData],
  );

  const filteredCategoryItems = useMemo(
    () =>
      categoryItems.filter(
        (c) =>
          c.name !== 'Non conformiteit' &&
          (usageClassification === c.usage ||
            c.usage === 'Both' ||
            usageClassification === 'None'),
      ),
    [categoryItems, usageClassification],
  );

  const subCategories = useMemo(
    () =>
      filteredCategoryItems
        ?.find((c) => c.id === category)
        ?.subCategories?.filter(
          (c) =>
            usageClassification === c.usage ||
            c.usage === 'Both' ||
            usageClassification === 'None',
        ),
    [category, filteredCategoryItems],
  );

  const selectedSubCategory = useMemo(
    () => subCategories?.find((subCat) => subCat.id === subCategory),
    [subCategory, subCategories],
  );

  const hasCustomerImpact = selectedSubCategory?.customerImpactMandatory;

  useEffect(() => {
    onAttachmentRequiredChange?.(!!selectedSubCategory?.attachmentRequired);
  }, [onAttachmentRequiredChange, selectedSubCategory?.attachmentRequired]);

  return (
    <>
      <FormDropdown
        label={t('common.main_category')}
        fullWidth
        name="mainCategoryId"
        options={
          filteredCategoryItems?.map((cat) => ({
            label: cat.name,
            value: cat.id,
          })) || []
        }
        rules={{ required: true }}
        isLoading={isLoadingCategories}
        onChange={() => {
          resetField('subCategoryId');
        }}
      />

      <FormDropdown
        key={category}
        fullWidth
        label={t('common.sub_category')}
        name="subCategoryId"
        options={
          subCategories?.map((subCat) => ({
            label: subCat.name,
            value: subCat.id,
          })) || []
        }
        rules={{ required: true }}
        disabled={!category}
        isLoading={isLoadingCategories}
        placeholder={
          category
            ? t('common.dropdown.select')
            : transtic('create.categories.select_main_category_first')
        }
      />
      {hasCustomerImpact && (
        <FormDropdown
          fullWidth
          label={transtic('customer_impact')}
          name="customerImpact"
          options={[
            { label: t('common.yes'), value: CustomerImpact.Yes },
            { label: t('common.no'), value: CustomerImpact.No },
          ]}
          rules={{ required: true }}
        />
      )}
    </>
  );
};
